import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { ResourceApi } from 'src/app/shared/apis/resource.api';
import { FooterIconUrl, FooterList, LicensePic } from 'src/app/shared/interfaces/resource.interface';
import { CountryUtilsService } from 'src/app/shared/service/country.service';
import { LayoutService } from 'src/app/shared/service/layout.service';
import { LocaleService } from 'src/app/shared/service/locale.service';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { ToastService } from 'src/app/shared/service/toast.service';
import { footerListTitleIndex } from './footer.config';

@UntilDestroy()
@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent implements OnInit {
  page!: string;
  isH5!: boolean;
  simple!: boolean;
  disclaimer!: string;
  licensePic!: LicensePic[];
  isDarkMode: boolean = false;
  footerListTitleIndex = footerListTitleIndex;

  aboutUSList: FooterList = { type: 'AboutUs', name: '', detail: [] };
  productList: FooterList = { type: 'Product', name: '', detail: [] };
  studyList: FooterList = { type: 'Study', name: '', detail: [] };
  gamblingList: FooterList = {
    type: 'GamblingAddiction',
    name: '',
    detail: [],
  };
  helpList: FooterList = { type: 'Help', name: '', detail: [] };
  communityList: FooterList = { type: 'Community', name: '', detail: [] };
  footerList = [
    this.aboutUSList,
    this.productList,
    this.helpList,
    // this.communityList,
    this.studyList,
    // this.gamblingList,
  ];
  h5SelectedIndex?: number;
  FooterIconUrl = FooterIconUrl;

  /** 是否為正式站（pubs.com），頁面生命週期內固定不變 */
  // 業務展示期間暫時全站隱藏法遵文字/授權徽章，展示結束後恢復下面這行
  // readonly isOfficialSite =
  //   window.location.hostname === 'pubs.com' || window.location.hostname === 'www.pubs.com';
  readonly isOfficialSite = false;

  /** Route Center 連結的 title（後端動態資料，以此辨識） */
  private readonly ROUTE_CENTER_TITLE = 'Route Center';

  constructor(
    private layout: LayoutService,
    private appService: AppService,
    private dialog: MatDialog,
    private countryUtils: CountryUtilsService,
    private router: Router,
    private resourceApi: ResourceApi,
    private toast: ToastService,
    private localeService: LocaleService,
    private sanitizer: DomSanitizer,
    private localStorageService: LocalStorageService,
  ) {
    this.layout.isH5$.pipe(untilDestroyed(this)).subscribe(e => (this.isH5 = e));
    this.layout.page$.pipe(untilDestroyed(this)).subscribe(e => {
      this.page = e;
      this.simple = ['register', 'login', 'password'].includes(e);
    });
  }

  countries!: any[]; //lang全部信息
  /** 当前选中的语言 */
  selectedLang: string = '';
  currentYear: string = '';
  /** Footer数据缓存 */
  static footerCacheData: any;

  async ngOnInit() {
    this.currentYear = new Date().getFullYear().toString();
    this.appService.languages$.pipe(untilDestroyed(this)).subscribe(x => {
      if (!x || x.length === 0) return;
      this.countries = x.map(item => {
        return {
          label: item.name,
          value: item.code,
          img: `assets/images/media/flags/${this.countryUtils.getcountryClassName(item.code)}.svg`,
        };
      });

      this.selectedLang =
        this.countries.find(y => y.value.toLowerCase() == this.appService.languageCode.toLowerCase()).value || 'en-us';
    });
    const result = FooterComponent.footerCacheData ?? (await firstValueFrom(this.resourceApi.getFooter()));
    if (result?.data) {
      FooterComponent.footerCacheData = result;
      const footerData = result.data.footer[0];
      this.licensePic = result.data.license;
      this.disclaimer = this.localeService.brandNameReplace(footerData?.disclaimer?.disclaimer ?? '');

      this.licensePic.forEach(x => {
        if (x.licenseType == 'Code') {
          x.resourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(x.image);
        }
      });

      footerData?.info?.forEach((info: any) => {
        if (info.footerType === 'Community') {
          this.communityList.detail = info.detail;
          return;
        }

        const target = this.footerList.find(f => f.type === info.footerType);
        if (!target) return;

        target.name = info.footerType;
        // Route Center 只在非正式站顯示：正式站就把它濾掉
        target.detail = this.isOfficialSite
          ? (info.detail ?? []).filter((d: any) => d?.title !== this.ROUTE_CENTER_TITLE)
          : info.detail;
      });

      // 註冊時的條款從這邊抓取，不從翻譯配置
      this.appService.termsConditionsLink = this.footerList.find(item => item.type === 'Study')?.detail?.[0]?.url || '';
      this.appService.privacyPolicyLink = this.footerList.find(item => item.type === 'Study')?.detail?.[1]?.url || '';
    }
  }

  ngAfterViewInit(): void {
    this.tryInitDlagSeal();
  }

  private tryInitDlagSeal(attempt: number = 1): void {
    const maxAttempts = 5;
    const retryDelayMs = 300;
    if (!this.isOfficialSite) {
      return;
    }
    const seal = (window as any).dlag_9bd36c32_a014_44a5_8268_d67f6c8748b0;
    const sealElId = 'dlag-9bd36c32-a014-44a5-8268-d67f6c8748b0';
    const canInit = this.isOfficialSite && document.getElementById(sealElId) && typeof seal?.init === 'function';
    if (canInit) {
      try {
        seal.init();
      } catch {
        if (attempt < maxAttempts) {
          setTimeout(() => this.tryInitDlagSeal(attempt + 1), retryDelayMs);
        }
      }
      return;
    }

    if (attempt < maxAttempts) {
      setTimeout(() => this.tryInitDlagSeal(attempt + 1), retryDelayMs);
    }
  }

  /**
   * 获取当前选中国家的语言图标
   * @returns
   */
  countryClassName() {
    if (!this.selectedLang) return '';
    return this.countryUtils.getcountryClassName(this.selectedLang);
  }

  onSelected(i: number) {
    if (this.h5SelectedIndex === i) {
      this.h5SelectedIndex = -1;
      return;
    }
    this.h5SelectedIndex = i;
  }

  getIsLink(url: string) {
    return url.slice(0, 4) === 'http';
  }

  jumpToPage(page: string, isBlank: boolean) {
    if (page == '/online-service') {
      this.appService.toOnLineService$.next(true);
      return;
    }
    if (!page) {
      this.toast.show({ message: `${this.localeService.getValue('page_exist')}!`, type: 'fail' });
      return;
    }
    let isCode: boolean = false;
    this.countries
      .map(v => v.code)
      .forEach(e => {
        page
          .split('/')
          .filter(a => a)
          .forEach((b: any) => {
            if (e === b) {
              isCode = true;
            }
          });
      });

    const url = page
      .split('/')
      .slice(isCode ? 2 : 1)
      .join('/');

    if (isBlank) {
      window.open(`${location.origin}/${this.appService.languageCode}/` + url, '_blank');
    } else {
      this.router.navigateByUrl(`/${this.appService.languageCode}/` + url);
    }
  }
  jumpToHome() {
    this.router.navigate(['/', this.appService.languageCode]);
  }

  jumpToGCB() {
    const token =
      'ZXlKcGRpSTZJbWRoSzA5VmRWWkxZbVl2ZFZSV016SXhhemcwVTNjOVBTSXNJblpoYkhWbElqb2laemRuUjJ4MloyTmxlbE5oZG1oemFXZHdiQzlCVVQwOUlpd2liV0ZqSWpvaU1UQXpOVEJoWkRCak1qaGtOekV4TlRZM1pUWm1Oek0wTXpjeE9ESTVaV1ZtTW1Kak1HRTJZbVZoWkdJNU9UWTBPRGczTURnNE1USTVZbUV4WW1VM09TSXNJblJoWnlJNklpSjk=';
    window.open(`https://cert.gcb.cw/certificate?id=${token}`, '_blank');
  }

  selectLang(item: string) {
    const targetCode = item.toLowerCase();
    if (this.appService.languageCode === targetCode) return;
    this.appService.selectLangFun(targetCode);
  }

  convertFooterListTitle(name: string) {
    return footerListTitleIndex[name];
  }
}
